//@flow
import * as React from 'react';
import { Icon, Position, Box, Button, Flex, Text } from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';
import theme from './Incremental.scss';
import { dashboardColors } from 'common/enums/ColorEnum';
import { MenuItem } from 'common/components/core/menu';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';

const ROWS_SELECTED_TYPE = Object.freeze({
    ALL: 'ALL',
    SOME: 'SOME',
    NONE: 'NONE'
});

const HeaderIcon = ({
    rowSelectedType,
    disabled
}: {
    rowSelectedType: $Values<typeof ROWS_SELECTED_TYPE>,
    disabled: boolean
}) => {
    let icon = (
        <Position position="absolute" bottom={0} left={0} right={0} top={-3}>
            <Icon icon="minus" size={Icon.Size.small} color="white" />
        </Position>
    );
    if (rowSelectedType === ROWS_SELECTED_TYPE.ALL) {
        icon = (
            <Position
                position="absolute"
                bottom={0}
                left={-2}
                right={0}
                top={-2}
            >
                <Icon icon="check" size={Icon.Size.medium} color="white" />
            </Position>
        );
    } else if (rowSelectedType === ROWS_SELECTED_TYPE.NONE) {
        icon = '';
    }
    return (
        <Position
            position="relative"
            height={14}
            width={14}
            bg={(() => {
                if (disabled) {
                    return 'grey.600';
                }
                if (rowSelectedType === ROWS_SELECTED_TYPE.NONE) {
                    return 'while';
                }
                return 'blue.500';
            })()}
            sx={{
                borderRadius: 2,
                border: 'thin solid',
                borderColor:
                    rowSelectedType === ROWS_SELECTED_TYPE.NONE
                        ? 'grey.400'
                        : 'blue.500'
            }}
        >
            {icon}
        </Position>
    );
};

type PropTypes = {
    rowSelectedType: $Values<typeof ROWS_SELECTED_TYPE>,
    toggleSelectPage: (checked: boolean) => void,
    toggleSelectAll: (checked: boolean) => void,
    disabled: boolean,
    isNewEntitiesTable: boolean
};

const HeaderCheckboxWithDropdown = ({
    rowSelectedType,
    toggleSelectPage,
    toggleSelectAll,
    disabled,
    isNewEntitiesTable
}: PropTypes): React.Node => {
    const { t } = useTranslation('entityManagement');
    const [isMenuVisible, setIsMenuVisible] = React.useState(false);
    const onMenuToggle = React.useCallback(() => {
        setIsMenuVisible(visible => !visible);
    }, []);

    const _onAllCheckPopoverMenuClick = React.useCallback(
        (e, { props: { value: id } }) => {
            onMenuToggle();
            switch (id) {
                case ROWS_SELECTED_TYPE.ALL:
                    rowSelectedType !== ROWS_SELECTED_TYPE.ALL &&
                        toggleSelectAll(true);
                    break;
                case ROWS_SELECTED_TYPE.NONE:
                    rowSelectedType !== ROWS_SELECTED_TYPE.NONE &&
                        toggleSelectAll(false);
                    break;
                case ROWS_SELECTED_TYPE.SOME:
                    toggleSelectPage(true);
                    break;
            }
        },
        [onMenuToggle, rowSelectedType, toggleSelectAll, toggleSelectPage]
    );

    const buttonComponent = (
        <Button appearance="ghost" disabled={disabled} onClick={onMenuToggle}>
            <Flex alignItems="center" justifyContent="space-between">
                <HeaderIcon
                    rowSelectedType={rowSelectedType}
                    disabled={disabled}
                />
                <Icon
                    icon="arrow_drop_down"
                    size={Icon.Size.Large}
                    color="grey.600"
                    ml={0.5}
                />
            </Flex>
        </Button>
    );

    if (disabled) {
        return buttonComponent;
    }

    return (
        <DivWithPopover
            isPopOverOpen={isMenuVisible}
            onOuterAction={onMenuToggle}
            popOverContent={
                <Box backgroundColor={dashboardColors.white.code} width="100px">
                    {[
                        {
                            label: isNewEntitiesTable
                                ? t('pjpCreate.incrementalPlan.none')
                                : t('pjpCreate.incrementalPlan.removeAll'),
                            id: ROWS_SELECTED_TYPE.NONE
                        },
                        {
                            label: isNewEntitiesTable
                                ? t('pjpCreate.incrementalPlan.currentPage')
                                : t('pjpCreate.incrementalPlan.removePage'),
                            id: ROWS_SELECTED_TYPE.SOME
                        },
                        {
                            label: isNewEntitiesTable
                                ? t('pjpCreate.incrementalPlan.all')
                                : t('pjpCreate.incrementalPlan.addAll'),
                            id: ROWS_SELECTED_TYPE.ALL
                        }
                    ].map(option => (
                        <MenuItem
                            ripple={false}
                            className={theme.pageOptions}
                            theme={theme}
                            key={option.id}
                            value={option.id}
                            caption={
                                <Text fontSize={1} fontWeight="medium">
                                    {option.label}
                                </Text>
                            }
                            onClick={_onAllCheckPopoverMenuClick}
                        />
                    ))}
                </Box>
            }
            place={PopoverPlaceEnum.Below}
            invertPopover={false}
        >
            {buttonComponent}
        </DivWithPopover>
    );
};

HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE = ROWS_SELECTED_TYPE;

export default HeaderCheckboxWithDropdown;
